import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api';

// Keep in sync with Uploads::MAX_UPLOAD_BYTES on the backend.
const MAX_UPLOAD_MB = 50;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

interface JacketUploadProps {
  /** When set, the file is attached to this existing jacket; otherwise a new
   *  paper jacket is created and we navigate to its edit page. */
  jacketId?: number;
  /** Called after a successful attachment upload (existing jacket). */
  onUploaded?: () => void;
}

/**
 * Paper-jacket / attachment uploader. Picks a PDF and uploads it in chunks.
 * - New (no jacketId): creates the paper jacket → navigates to /jackets/{id}/edit.
 * - Existing: attaches the document to the jacket.
 */
export function JacketUpload({ jacketId, onUploaded }: JacketUploadProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [runOcr, setRunOcr] = useState(true);

  const pick = (f: File | null) => {
    setError(null);
    setDone(null);
    setProgress(0);
    if (f && f.type && f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      setError('Please choose a PDF file.');
      setFile(null);
      return;
    }
    // Mirror the server cap (Uploads::MAX_UPLOAD_BYTES) so we don't stream a
    // file that the backend will reject.
    if (f && f.size > MAX_UPLOAD_BYTES) {
      setError(`File is too large (max ${MAX_UPLOAD_MB} MB).`);
      setFile(null);
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setProgress(0);
    try {
      if (jacketId) {
        await api.uploadJacketAttachment(jacketId, file, setProgress);
        setDone(`Attached “${file.name}”.`);
        setFile(null);
        if (inputRef.current) inputRef.current.value = '';
        onUploaded?.();
      } else {
        const { jacketId: newId, ocrApplied } = await api.uploadPaperJacket(file, setProgress, runOcr);
        // Replace the uploader entry so a later Save/Back (navigate(-1)) returns
        // to the jackets list, not back to this uploader page.
        navigate(`/jackets/${newId}/edit${ocrApplied ? '?ocr=done' : ''}`, { replace: true });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ast-uploader">
      <div className="ast-dropzone" onClick={() => inputRef.current?.click()} role="button" tabIndex={0}>
        <i className="bi bi-cloud-arrow-up" />
        <div>{file ? file.name : 'Choose a PDF to upload'}</div>
        <small className="text-muted">PDF only</small>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="d-none"
          onChange={(e) => pick(e.target.files?.[0] ?? null)}
        />
      </div>

      {!jacketId && (
        <div className="form-check ast-ocr-toggle">
          <input
            id="run-ocr"
            type="checkbox"
            className="form-check-input"
            checked={runOcr}
            disabled={busy}
            onChange={(e) => setRunOcr(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="run-ocr">
            Scan document with OCR to auto-fill the jacket
          </label>
        </div>
      )}

      {busy && (
        <div className="progress ast-upload-progress" role="progressbar" aria-valuenow={Math.round(progress * 100)}>
          <div className="progress-bar" style={{ width: `${Math.round(progress * 100)}%` }}>
            {Math.round(progress * 100)}%
          </div>
        </div>
      )}
      {busy && progress >= 1 && !jacketId && runOcr && (
        <div className="text-muted small mt-2">
          <i className="bi bi-stars" /> Reading the document with AI to fill in the jacket…
        </div>
      )}
      {error && <div className="ast-field-error mt-2">{error}</div>}
      {done && <div className="alert alert-success py-2 mt-2 mb-0">{done}</div>}

      <div className="ast-upload-actions">
        <button type="button" className="btn btn-ast-blue" disabled={!file || busy} onClick={upload}>
          {busy ? 'Uploading…' : jacketId ? 'Upload Attachment' : 'Upload & Create Jacket'}
        </button>
      </div>
    </div>
  );
}
